/**
 * ApiTester Component - Redesigned
 *
 * Modern step-by-step interface for API testing with improved UX.
 * Features: Progressive disclosure, better visual feedback, cleaner layout.
 *
 * Requirements: 3.5, 5.1, 10.1, 11.1-11.6, 15.1, 19.1, 19.2
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ApiTesterProps,
  ParsedEndpoint,
  ExternalCredentials,
  ApiTestRequest,
  ApiTestResponse,
  RequestHistoryEntry,
} from '../types';
import { parseOpenApiSpec, validateCredentialFields, sendTestRequest } from '../utils';
import { useRequestHistory } from '../hooks';
import {
  ApplicationSelector,
  EnvironmentSelector,
  EndpointSelector,
  CredentialFields,
  PathParameterInputs,
  QueryParameterInputs,
  RequestBodyEditor,
} from './';
import { ResponseDisplay } from './ResponseDisplay';
import { RequestHistory } from './RequestHistory';
import { Alert } from '../../../components/ui/Alert';

/**
 * ApiTester component provides a complete interface for testing API endpoints
 */
export const ApiTester: React.FC<ApiTesterProps> = ({
  product: initialProduct,
  applications,
  initialEndpoint,
}) => {
  // State management
  const [product, setProduct] = useState(initialProduct);
  const [selectedApplication, setSelectedApplication] = useState<{
    id: string;
    name: string;
    api_key: string;
  } | null>(applications[0] || null);

  useEffect(() => {
    setProduct(initialProduct);
  }, [initialProduct]);

  useEffect(() => {
    if (applications && applications.length > 0) {
      setSelectedApplication((prev) => {
        if (!prev || !prev.api_key) return applications[0];
        const match = applications.find((a) => a.id === prev.id);
        return match || applications[0];
      });
    }
  }, [applications]);
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const [endpoints, setEndpoints] = useState<ParsedEndpoint[]>([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState<ParsedEndpoint | null>(null);
  const [externalCredentials, setExternalCredentials] = useState<ExternalCredentials>({});
  const [pathParams, setPathParams] = useState<Record<string, string>>({});
  const [queryParams, setQueryParams] = useState<Record<string, string>>({});
  const [requestBody, setRequestBody] = useState<string>('{}');
  const [response, setResponse] = useState<ApiTestResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  // Request history hook
  const { history, addHistoryEntry } = useRequestHistory();

  /**
   * Parse OpenAPI spec when product changes
   */
  useEffect(() => {
    if (product?.openapi_spec) {
      const parsedEndpoints = parseOpenApiSpec(product.openapi_spec);
      setEndpoints(parsedEndpoints);
    } else {
      setEndpoints([]);
    }
  }, [product]);

  /**
   * Initialize path and query parameters when endpoint changes
   */
  useEffect(() => {
    if (selectedEndpoint) {
      // Initialize path parameters
      const pathParamNames = selectedEndpoint.parameters
        .filter((p) => p.in === 'path')
        .reduce((acc, p) => ({ ...acc, [p.name]: '' }), {});
      setPathParams(pathParamNames);

      // Initialize query parameters
      const queryParamNames = selectedEndpoint.parameters
        .filter((p) => p.in === 'query')
        .reduce((acc, p) => ({ ...acc, [p.name]: '' }), {});
      setQueryParams(queryParamNames);

      // Reset request body for GET/DELETE
      if (selectedEndpoint.method === 'GET' || selectedEndpoint.method === 'DELETE') {
        setRequestBody('{}');
      }
    }
  }, [selectedEndpoint]);

  /**
   * Handle request body changes
   */
  const handleRequestBodyChange = useCallback((value: string) => {
    setRequestBody(value);
  }, []);

  /**
   * Handle history entry selection for replay (Requirements 11.1-11.6)
   */
  const handleHistorySelect = useCallback((entry: RequestHistoryEntry) => {
    // Populate endpoint selection
    setSelectedEndpoint(entry.request.endpoint);

    // Populate credential fields
    setExternalCredentials(entry.request.externalCredentials);

    // Populate path parameters
    if (entry.request.pathParams) {
      setPathParams(entry.request.pathParams);
    }

    // Populate query parameters
    if (entry.request.queryParams) {
      setQueryParams(entry.request.queryParams);
    }

    // Populate request body
    if (entry.request.requestBody) {
      setRequestBody(JSON.stringify(entry.request.requestBody, null, 2));
    }

    // Set environment
    setEnvironment(entry.request.environment);

    // Note: We do NOT automatically send the request (Requirement 11.6)
  }, []);

  /**
   * Validate and send test request (Requirements 3.5, 5.1)
   */
  const handleSendRequest = async () => {
    setValidationError('');

    // Validate endpoint selection
    if (!selectedEndpoint) {
      setValidationError('Please select an endpoint');
      return;
    }

    // Validate application selection
    if (!selectedApplication) {
      setValidationError('Please select an application');
      return;
    }

    // Validate external credentials (Requirement 3.5)
    if (product.requires_external_auth && product.external_auth_config) {
      const validation = validateCredentialFields(
        externalCredentials,
        product.external_auth_config.fields
      );

      if (!validation.valid) {
        setValidationError(`Missing required credentials: ${validation.missingFields.join(', ')}`);
        return;
      }
    }

    // Validate request body JSON for POST/PUT/PATCH
    let parsedBody: any = undefined;
    if (selectedEndpoint.requiresBody) {
      try {
        parsedBody = JSON.parse(requestBody);
      } catch (error) {
        setValidationError('Invalid JSON in request body');
        return;
      }
    }

    // Build test request
    const testRequest: ApiTestRequest = {
      productId: product.id,
      endpoint: selectedEndpoint,
      apiKey: selectedApplication.api_key,
      externalAuthType: product.external_auth_type || '',
      externalCredentials,
      requestBody: parsedBody,
      queryParams,
      pathParams,
      environment,
    };

    // Send request (Requirement 5.1)
    setIsLoading(true);
    try {
      const testResponse = await sendTestRequest(testRequest);
      setResponse(testResponse);

      // Add to history (Requirement 10.1)
      addHistoryEntry(testRequest, testResponse);
    } catch (error) {
      console.error('Error sending test request:', error);
      setValidationError('Failed to send request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-custom py-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Side (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="card-modern p-6">
            <h1
              className="text-gray-900 mb-2"
              style={{
                fontSize: 'var(--text-3xl)',
                fontWeight: 'var(--font-bold)',
              }}
            >
              🧪 Testar API
            </h1>
            <p className="text-gray-600" style={{ fontSize: 'var(--text-base)' }}>
              Configure e envie requisições para testar os endpoints da API
            </p>
          </div>

          {/* Step 1: Application & Environment */}
          <div className="card-modern p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700"
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                }}
              >
                1
              </div>
              <h2
                className="text-gray-900"
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-semibold)',
                }}
              >
                Aplicação e Ambiente
              </h2>
            </div>

            <div className="space-y-4">
              <ApplicationSelector
                applications={applications}
                selectedApplication={selectedApplication}
                onSelect={setSelectedApplication}
              />

              <EnvironmentSelector
                selectedEnvironment={environment}
                onEnvironmentChange={setEnvironment}
              />
            </div>
          </div>

          {/* Step 2: Endpoint Selection */}
          <div className="card-modern p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700"
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                }}
              >
                2
              </div>
              <h2
                className="text-gray-900"
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-semibold)',
                }}
              >
                Selecionar Endpoint
              </h2>
            </div>

            <EndpointSelector
              endpoints={endpoints}
              selectedEndpoint={selectedEndpoint}
              onSelect={setSelectedEndpoint}
              initialEndpointId={initialEndpoint}
            />

            {selectedEndpoint && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`badge-modern ${getMethodBadgeClass(selectedEndpoint.method)}`}>
                    {selectedEndpoint.method}
                  </span>
                  <code
                    className="text-gray-900"
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-sm)',
                    }}
                  >
                    {selectedEndpoint.path}
                  </code>
                </div>
                {selectedEndpoint.summary && (
                  <p className="text-gray-600" style={{ fontSize: 'var(--text-sm)' }}>
                    {selectedEndpoint.summary}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Step 3: Authentication (if required) */}
          {product.requires_external_auth && product.external_auth_config && (
            <div className="card-modern p-6">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700"
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                  }}
                >
                  3
                </div>
                <h2
                  className="text-gray-900"
                  style={{
                    fontSize: 'var(--text-xl)',
                    fontWeight: 'var(--font-semibold)',
                  }}
                >
                  Autenticação Externa
                </h2>
              </div>

              <Alert variant="info" className="mb-4">
                Esta API requer credenciais externas ({product.external_auth_type || 'Basic Auth'})
              </Alert>

              <CredentialFields
                config={product.external_auth_config}
                values={externalCredentials}
                onChange={setExternalCredentials}
                validationErrors={validationError ? [validationError] : []}
              />
            </div>
          )}

          {/* Step 4: Parameters */}
          {selectedEndpoint && (
            <>
              {/* Path Parameters */}
              {selectedEndpoint.parameters.filter((p) => p.in === 'path').length > 0 && (
                <div className="card-modern p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700"
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-semibold)',
                      }}
                    >
                      {product.requires_external_auth ? '4' : '3'}
                    </div>
                    <h2
                      className="text-gray-900"
                      style={{
                        fontSize: 'var(--text-xl)',
                        fontWeight: 'var(--font-semibold)',
                      }}
                    >
                      Parâmetros de Path
                    </h2>
                  </div>

                  <PathParameterInputs
                    parameters={selectedEndpoint.parameters.filter((p) => p.in === 'path')}
                    values={pathParams}
                    onChange={(values) => setPathParams(values)}
                  />
                </div>
              )}

              {/* Query Parameters */}
              {selectedEndpoint.parameters.filter((p) => p.in === 'query').length > 0 && (
                <div className="card-modern p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700"
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-semibold)',
                      }}
                    >
                      {getStepNumber('query')}
                    </div>
                    <h2
                      className="text-gray-900"
                      style={{
                        fontSize: 'var(--text-xl)',
                        fontWeight: 'var(--font-semibold)',
                      }}
                    >
                      Parâmetros de Query
                    </h2>
                  </div>

                  <QueryParameterInputs
                    parameters={selectedEndpoint.parameters.filter((p) => p.in === 'query')}
                    values={queryParams}
                    onChange={(values) => setQueryParams(values)}
                  />
                </div>
              )}

              {/* Request Body */}
              {selectedEndpoint.requiresBody && (
                <div className="card-modern p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700"
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-semibold)',
                      }}
                    >
                      {getStepNumber('body')}
                    </div>
                    <h2
                      className="text-gray-900"
                      style={{
                        fontSize: 'var(--text-xl)',
                        fontWeight: 'var(--font-semibold)',
                      }}
                    >
                      Corpo da Requisição
                    </h2>
                  </div>

                  <RequestBodyEditor
                    endpoint={selectedEndpoint}
                    value={requestBody}
                    onChange={handleRequestBodyChange}
                  />
                </div>
              )}
            </>
          )}

          {/* Validation Error */}
          {validationError && (
            <Alert variant="error" dismissible onDismiss={() => setValidationError('')}>
              <strong>Erro de Validação:</strong> {validationError}
            </Alert>
          )}

          {/* Send Request Button */}
          <div className="card-modern p-6">
            <button
              onClick={handleSendRequest}
              disabled={isLoading || !selectedEndpoint || !selectedApplication}
              className="btn-base btn-primary btn-lg w-full"
              style={{ fontSize: 'var(--text-lg)' }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="spinner" />
                  Enviando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 10L18 2L10 18L8 11L2 10Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Enviar Requisição
                </span>
              )}
            </button>
          </div>

          {/* Response Display */}
          {response && (
            <div className="card-modern p-6">
              <h2
                className="text-gray-900 mb-4"
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-semibold)',
                }}
              >
                Resposta
              </h2>
              <ResponseDisplay response={response} />
            </div>
          )}
        </div>

        {/* Sidebar - Request History (1/3) */}
        <div className="lg:col-span-1">
          <div className="card-modern p-6 sticky top-6">
            <RequestHistory history={history} onSelectEntry={handleHistorySelect} />
          </div>
        </div>
      </div>
    </div>
  );

  // Helper function to get step number dynamically
  function getStepNumber(section: 'query' | 'body'): number {
    let step = 3;
    if (product.requires_external_auth) step++;
    if (selectedEndpoint && selectedEndpoint.parameters.filter((p) => p.in === 'path').length > 0)
      step++;
    if (
      section === 'body' &&
      selectedEndpoint &&
      selectedEndpoint.parameters.filter((p) => p.in === 'query').length > 0
    )
      step++;
    return step;
  }

  // Helper function to get method badge class
  function getMethodBadgeClass(method: string): string {
    switch (method) {
      case 'GET':
        return 'badge-primary';
      case 'POST':
        return 'badge-success';
      case 'PUT':
      case 'PATCH':
        return 'badge-warning';
      case 'DELETE':
        return 'badge-error';
      default:
        return 'badge-gray';
    }
  }
};

export default ApiTester;
